import { useMemo, useState } from "react";
import { Search, Wallet } from "lucide-react";

import type { EstadoFactura } from "../api/tipos.ts";
import { usarLibro } from "../ganchos/datos.ts";
import { esElMontoBuscado, pesos, plural } from "../formato.ts";
import { ESTADO_FACTURA } from "../vocabulario.ts";
import { ErrorConsulta, EsqueletoTabla } from "../componentes/Errores.tsx";
import { Aviso, InsigniaDe, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

const ORDEN: Record<EstadoFactura, number> = { PENDIENTE: 0, PARCIAL: 1, PAGADA: 2 };

/**
 * Cartera. La regla que hay que tener presente al leerla: **solo cuentan los
 * abonos respaldados por el banco**. Un abono declarado que el banco no muestra
 * no baja la deuda — aparece en Alertas, que es donde sirve de algo.
 */
export function Cartera() {
  const { data: libro, isPending, error, refetch } = usarLibro();
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState<EstadoFactura | "todo">("todo");

  const facturas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return [...(libro?.facturas ?? [])]
      // Se busca por número, por nombre y por documento: quien tiene delante un
      // pago del banco conoce el nombre, no el número de factura.
      .filter(
        (f) =>
          (estado === "todo" || f.estado === estado) &&
          (esElMontoBuscado(q, f.totalCentavos) ||
            esElMontoBuscado(q, f.saldoCentavos) ||
            [f.numero, f.cliente, f.nit].some((v) => (v ?? "").toLowerCase().includes(q))),
      )
      .sort((a, b) => ORDEN[a.estado] - ORDEN[b.estado] || b.saldoCentavos - a.saldoCentavos);
  }, [libro?.facturas, busqueda, estado]);

  const totales = useMemo(() => {
    const t = libro?.facturas ?? [];
    return {
      total: t.reduce((s, f) => s + (f.totalCentavos ?? 0), 0),
      abonado: t.reduce((s, f) => s + f.abonadoCentavos, 0),
      saldo: t.reduce((s, f) => s + Math.max(0, f.saldoCentavos), 0),
      abiertas: t.filter((f) => f.estado !== "PAGADA").length,
    };
  }, [libro?.facturas]);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  // El backend omite `facturas` para el rol cajero: no es un fallo, es el diseño.
  if (!isPending && !libro?.facturas) {
    return (
      <Aviso tono="dato" titulo="La cartera no es parte de tu rol">
        Saber cuánto debe cada cliente no hace falta para confirmar un pago y entregar un pedido.
      </Aviso>
    );
  }

  return (
    <Seccion
      titulo="Cartera"
      descripcion="Cuánto queda por cobrar, contando solo los pagos que el banco confirma."
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Total titulo="Facturado" valor={pesos(totales.total)} />
        <Total titulo="Recaudado y respaldado" valor={pesos(totales.abonado)} tono="marca" />
        <Total
          titulo="Por cobrar"
          valor={pesos(totales.saldo)}
          tono="aviso"
          nota={`${totales.abiertas} ${plural(totales.abiertas, "factura abierta", "facturas abiertas")}`}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue-2"
            aria-hidden
          />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por factura, cliente o NIT…"
            className="h-10 w-full rounded-xl border border-borde bg-superficie pr-3.5 pl-10 text-sm placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
          />
        </div>
        <select
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoFactura | "todo")}
          className="h-10 rounded-xl border border-borde bg-superficie px-3 text-sm focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        >
          <option value="todo">Todas</option>
          {(["PENDIENTE", "PARCIAL", "PAGADA"] as EstadoFactura[]).map((e) => (
            <option key={e} value={e}>
              {ESTADO_FACTURA[e].texto}
            </option>
          ))}
        </select>
      </div>

      <Tarjeta className="overflow-hidden">
        {isPending ? (
          <div className="p-4">
            <EsqueletoTabla />
          </div>
        ) : facturas.length === 0 ? (
          <Vacio icono={Wallet} titulo="Sin facturas que mostrar">
            {libro?.facturas?.length
              ? "Ninguna coincide con el filtro."
              : "Todavía no hay facturas cargadas para esta cuenta."}
          </Vacio>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borde text-left text-xs tracking-wide text-tenue uppercase">
                  <th className="px-4 py-3 font-medium">Factura</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 text-right font-medium">Total</th>
                  <th className="px-4 py-3 text-right font-medium">Abonado</th>
                  <th className="px-4 py-3 text-right font-medium">Saldo</th>
                  <th className="w-40 px-4 py-3 font-medium">Respaldado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borde">
                {facturas.map((f) => {
                  // Lo cobrado es lo que el banco respaldó: por eso va en teal,
                  // que es el color reservado a lo que cuadró. Lo que falta se
                  // deja del verde de tu lado, apagado — todavía es tuyo por
                  // cobrar, no un error.
                  const cobrado = f.totalCentavos
                    ? Math.min(100, (f.abonadoCentavos / f.totalCentavos) * 100)
                    : 0;
                  return (
                    <tr key={f.numero} className="transition-colors hover:bg-superficie-2">
                      <td className="cifra px-4 py-3 font-medium whitespace-nowrap">{f.numero}</td>
                      {/* El nombre se lee; el documento identifica. Se muestra
                          debajo y en pequeño porque es lo que hay que copiar
                          cuando alguien cruza esto contra otro sistema. */}
                      <td className="px-4 py-3">
                        {f.cliente ? (
                          <span className="block max-w-56 truncate text-tinta">{f.cliente}</span>
                        ) : (
                          <span className="text-tenue-2">—</span>
                        )}
                        {f.nit && <span className="cifra block text-xs text-tenue">{f.nit}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <InsigniaDe etiqueta={ESTADO_FACTURA[f.estado]} />
                      </td>
                      <td className="cifra px-4 py-3 text-right whitespace-nowrap text-tenue">
                        {pesos(f.totalCentavos)}
                      </td>
                      <td
                        className={cx(
                          "cifra px-4 py-3 text-right font-medium whitespace-nowrap",
                          // Cero respaldado no es un cuadre: el teal está
                          // reservado a lo que de verdad se tocó.
                          f.abonadoCentavos > 0 ? "text-marca" : "text-tenue-2",
                        )}
                      >
                        {pesos(f.abonadoCentavos)}
                      </td>
                      <td
                        className={cx(
                          "cifra px-4 py-3 text-right font-medium whitespace-nowrap",
                          f.saldoCentavos > 0 ? "text-aviso-tinta" : "text-marca",
                        )}
                      >
                        {pesos(f.saldoCentavos)}
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="flex h-2.5 overflow-hidden rounded-full bg-exito/15"
                          role="img"
                          aria-label={`${Math.round(cobrado)}% respaldado por el banco`}
                        >
                          <div className="bg-marca" style={{ width: `${cobrado}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </Seccion>
  );
}

const Total = ({
  titulo,
  valor,
  nota,
  tono = "neutro",
}: {
  titulo: string;
  valor: string;
  nota?: string;
  tono?: "neutro" | "marca" | "aviso";
}) => (
  <Tarjeta className="space-y-1.5 p-5">
    <p className="text-sm text-tenue">{titulo}</p>
    <p
      className={cx(
        // Nunito y extrabold: es una cifra, y las cifras son el producto.
        "cifra-titular text-2xl",
        // `marca` es el teal del cuadre: lo que el banco respaldó de verdad.
        tono === "marca" && "text-marca",
        tono === "aviso" && "text-aviso-tinta",
      )}
    >
      {valor}
    </p>
    {nota && <p className="text-xs text-tenue-2">{nota}</p>}
  </Tarjeta>
);
